import React from 'react'
import { Box, Alert, Typography, Button, CircularProgress } from '@mui/material'
import { useAppAccess } from '@/hooks/useAppAccess'
import { useAuthContext } from '@contexts/AuthContext'
import { APP_DISPLAY_NAMES, type AppKey } from '@/config/appKeys'

interface RequireAccessProps {
  app: AppKey
  children: React.ReactNode
}

/**
 * Phase 76: App-level access gate component.
 *
 * MUST be used inside ProtectedRoute (which provides authentication,
 * boot screen, and Layout). This component does NOT wrap children
 * in <Layout> — ProtectedRoute already does that via <Outlet />.
 *
 * Replaces AdminRoute for app-level gating. AdminRoute checked is_admin;
 * RequireAccess checks app_access for the specific app key.
 *
 * Usage:
 *   <Route element={<ProtectedRoute />}>
 *     <Route path="/knowledge" element={
 *       <RequireAccess app="knowledge"><KnowledgePage /></RequireAccess>
 *     } />
 *   </Route>
 */
const RequireAccess: React.FC<RequireAccessProps> = ({ app, children }) => {
  const { hasAccess, isLoading } = useAppAccess()
  const { user, logout } = useAuthContext()

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress size={40} />
      </Box>
    )
  }

  if (!hasAccess(app)) {
    // No <Layout> wrapper — we're already inside ProtectedRoute's Layout
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
        p={3}
      >
        <Alert
          severity="error"
          sx={{ mb: 3, maxWidth: 600 }}
        >
          <Typography variant="h6" gutterBottom>
            Acceso denegado
          </Typography>
          <Typography variant="body1">
            No tienes acceso a esta aplicación. Contacta a un administrador para solicitar acceso.
          </Typography>
        </Alert>

        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            onClick={() => window.history.back()}
          >
            Volver
          </Button>
          <Button
            variant="outlined"
            onClick={logout}
          >
            Cerrar Sesión
          </Button>
        </Box>

        <Box mt={2}>
          <Typography variant="body" color="text.secondary">
            Nivel de acceso actual: {user?.roles?.join(', ') || 'usuario'} — Se requiere acceso a: {APP_DISPLAY_NAMES[app] || app}
          </Typography>
        </Box>
      </Box>
    )
  }

  // Children render directly — Layout is provided by ProtectedRoute
  return <>{children}</>
}

export default RequireAccess
